'use client'

import Script from 'next/script'
import { usePathname, useSearchParams } from 'next/navigation'
import { useEffect, useState } from 'react'

interface MarketingSettings {
    facebook?: { enabled: boolean; pixelId: string };
    tiktok?: { enabled: boolean; pixelId: string };
    snapchat?: { enabled: boolean; pixelId: string };
    google?: { enabled: boolean; measurementId: string };
}

export function MarketingPixels({ settings }: { settings: MarketingSettings }) {
    const pathname = usePathname()
    const searchParams = useSearchParams()
    const [hasConsent, setHasConsent] = useState(false)

    useEffect(() => {
        if (typeof document === 'undefined') return
        const checkConsent = () => document.cookie.includes('ecommaps_marketing_consent=1')
        setHasConsent(checkConsent())
        // Re-check on navigation in case consent was granted elsewhere
        const interval = setInterval(() => setHasConsent(checkConsent()), 2000)
        return () => clearInterval(interval)
    }, [])

    // Page View Tracking Trigger
    // We can use this effect to trigger custom page_view events if standard scripts don't catch SPA transitions
    useEffect(() => {
        // Facebook PageView is auto-triggered by the script on load, 
        // but for SPA transitions next/script usually handles it if configured correctly.
        // If not, we can manually trigger fbq('track', 'PageView') here.
        if (settings.facebook?.enabled && typeof window !== 'undefined' && (window as any).fbq) {
            (window as any).fbq('track', 'PageView')
        }
    }, [pathname, searchParams, settings.facebook])

    if (!settings || !hasConsent) return null;

    return (
        <>
            {/* ════════════════════════════════════════════════════════════
                Meta / Facebook Pixel
               ════════════════════════════════════════════════════════════ */}
            {settings.facebook?.enabled && settings.facebook.pixelId && (
                <Script
                    id="fb-pixel"
                    strategy="afterInteractive"
                    dangerouslySetInnerHTML={{
                        __html: `
                        !function(f,b,e,v,n,t,s)
                        {if(f.fbq)return;n=f.fbq=function(){n.callMethod?
                        n.callMethod.apply(n,arguments):n.queue.push(arguments)};
                        if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
                        n.queue=[];t=b.createElement(e);t.async=!0;
                        t.src=v;s=b.getElementsByTagName(e)[0];
                        s.parentNode.insertBefore(t,s)}(window, document,'script',
                        'https://connect.facebook.net/en_US/fbevents.js');
                        fbq('init', '${settings.facebook.pixelId}');
                        fbq('track', 'PageView');
                        `,
                    }}
                />
            )}

            {/* ════════════════════════════════════════════════════════════
                Google Analytics 4
               ════════════════════════════════════════════════════════════ */}
            {settings.google?.enabled && settings.google.measurementId && (
                <>
                    <Script
                        strategy="afterInteractive"
                        src={`https://www.googletagmanager.com/gtag/js?id=${settings.google.measurementId}`}
                    />
                    <Script
                        id="google-analytics"
                        strategy="afterInteractive"
                        dangerouslySetInnerHTML={{
                            __html: `
                            window.dataLayer = window.dataLayer || [];
                            function gtag(){dataLayer.push(arguments);}
                            gtag('js', new Date());
                            gtag('config', '${settings.google.measurementId}', {
                                page_path: window.location.pathname,
                            });
                            `,
                        }}
                    />
                </>
            )}

            {/* ════════════════════════════════════════════════════════════
                TikTok Pixel
               ════════════════════════════════════════════════════════════ */}
            {settings.tiktok?.enabled && settings.tiktok.pixelId && (
                <Script
                    id="tiktok-pixel"
                    strategy="afterInteractive"
                    dangerouslySetInnerHTML={{
                        __html: `
                        !function (w, d, t) {
                            w.TiktokAnalyticsObject=t;var ttq=w[t]=w[t]||[];ttq.methods=["page","track","identify","instances","debug","on","off","once","ready","alias","group","enableCookie","disableCookie"],ttq.setAndDefer=function(t,e){t[e]=function(){t.push([e].concat(Array.prototype.slice.call(arguments,0)))}};for(var i=0;i<ttq.methods.length;i++)ttq.setAndDefer(ttq,ttq.methods[i]);ttq.instance=function(t){for(var e=ttq._i[t]||[],n=0;n<ttq.methods.length;n++)ttq.setAndDefer(e,ttq.methods[n]);return e},ttq.load=function(e,n){var i="https://analytics.tiktok.com/i18n/pixel/events.js";ttq._i=ttq._i||{},ttq._i[e]=[],ttq._i[e]._u=i,ttq._t=ttq._t||{},ttq._t[e]=+new Date,ttq._o=ttq._o||{},ttq._o[e]=n||{};var o=document.createElement("script");o.type="text/javascript",o.async=!0,o.src=i+"?sdkid="+e+"&lib="+t;var a=document.getElementsByTagName("script")[0];a.parentNode.insertBefore(o,a)};
                            ttq.load('${settings.tiktok.pixelId}');
                            ttq.page();
                        }(window, document, 'ttq');
                        `,
                    }}
                />
            )}

            {/* ════════════════════════════════════════════════════════════
                Snapchat Pixel
               ════════════════════════════════════════════════════════════ */}
            {settings.snapchat?.enabled && settings.snapchat.pixelId && (
                <Script
                    id="snap-pixel"
                    strategy="afterInteractive"
                    dangerouslySetInnerHTML={{
                        __html: `
                        (function(e,t,n){if(e.snaptr)return;var a=e.snaptr=function()
                        {a.handleRequest?a.handleRequest.apply(a,arguments):a.queue.push(arguments)};
                        a.queue=[];var s='script';var r=t.createElement(s);r.async=!0;
                        r.src=n;var u=t.getElementsByTagName(s)[0];
                        u.parentNode.insertBefore(r,u);})(window,document,
                        'https://sc-static.net/scevent.min.js');

                        snaptr('init', '${settings.snapchat.pixelId}');
                        snaptr('track', 'PAGE_VIEW');
                        `,
                    }}
                />
            )}
        </>
    )
}
